import * as Yup from 'yup';
import { App } from 'App';
import { AppRegistrationSection } from 'AppRegistrationSection';
import {
  Button,
  ButtonActor,
  CheckboxActor,
  InputActor,
  Row,
  Spacer,
  Text,
  Title,
} from 'components';
import { CheckIcon, LockIcon, ShieldIcon } from 'components/icons';
import { createTransLinksComponents } from '../../utils/transLinksComponents';
import { emailRegExp, mobilePhoneNumberValid } from 'utils/regularExpressions';
import { Frame } from 'components/organisms';
import { frameConfig } from 'AppConfig';
import { getFetchHeaders } from 'utils/fetchHeaders';
import { isInWebView } from '../../utils/isInWebView';
import { phoneSanitizer } from '../../utils/sanitizers';
import { ROUTES } from 'utils/constants/constants_routes';
import { Trans, useTranslation } from 'react-i18next';
import { useApplicationSummary } from 'hooks/useApplicationSummary';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useErrorManager } from 'InternalLibrary/app';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { usePostLeadsRegister } from 'api';
import { validateEmailDomain } from 'validators/validateEmailDomain';
import classes from 'classnames';
import layout from 'scss/layout.module.scss';
import LegalTermsModal from 'components/atoms/LegalTermsModal/LegalTermsModal';
import styles from './Identification.module.scss';

export interface IdentificationForm {
  email: string;
  phone: string;
  agreements: Record<string, boolean>;
}

export default function Identification() {
  const navigate = useNavigate();
  const apiLeadsRegister = usePostLeadsRegister();
  const { setError } = useErrorManager();
  const i18next = useTranslation();

  const [showModal, setShowModal] = useState(false);

  const handleModal = () => setShowModal(!showModal);

  const initialValues: Partial<IdentificationForm> | undefined = useMemo(() => {
    const ssLeadStr = sessionStorage.getItem('lead');

    const defaultValues = {
      email: '',
      phone: '',
      agreements: {
        firstAgreement: false,
        secondAgreement: false,
        thirdAgreement: false,
      },
    };

    if (!ssLeadStr) {
      return defaultValues;
    }
    const { email, mobilePhone, agreements } = JSON.parse(ssLeadStr);

    return {
      email,
      phone: mobilePhone,
      agreements: {
        firstAgreement: agreements?.firstAgreement,
        secondAgreement: agreements?.secondAgreement,
        thirdAgreement: agreements?.thirdAgreement,
      },
    };
  }, []);

  const dataMatchesInitial = useCallback(
    (data: IdentificationForm) => {
      if (!initialValues) {
        return false;
      }
      const { email, phone, agreements } = initialValues;

      return (
        data.email === email &&
        data.phone === phone &&
        data.agreements.firstAgreement === agreements?.firstAgreement &&
        data.agreements.secondAgreement === agreements?.secondAgreement &&
        data.agreements.thirdAgreement === agreements?.thirdAgreement
      );
    },

    [initialValues],
  );

  const onSubmit = async (data: any) => {
    formik.setStatus({ errors: {} });
    setLoading(true);
    try {
      if (!dataMatchesInitial(data)) {
        await apiLeadsRegister.fetch({
          headers: getFetchHeaders(),
          body: {
            email: data.email,
            mobilePhone: data.phone,
            acceptNews: data.agreements?.firstAgreement,
            acceptProfiling: data?.agreements?.secondAgreement, // In COLA this is mapped to Data Sharing
            acceptAgreement: data?.agreements?.thirdAgreement,
            originator: 'UNFINISHED_REGISTRATION',
            productKind: App.config.productKind,
          },
        });
        sessionStorage.setItem(
          'lead',
          JSON.stringify({
            email: data.email,
            mobilePhone: data.phone,
            agreements: {
              firstAgreement: data.agreements.firstAgreement,
              secondAgreement: data.agreements.secondAgreement,
              thirdAgreement: data.agreements.thirdAgreement,
            },
          }),
        );
      }
      navigate(ROUTES.FIRST_LOAN_CONFIRMATION);
    } catch (error) {
      if (error.status === 400) {
        setError('wrongEmailPhone');
      } else if (error.status === 403) {
        setError('Forbidden');
      } else {
        setError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const { amount, term } = useApplicationSummary();

  const [loading, setLoading] = useState(false);

  const { t } = useTranslation('identification');

  const validationSchema = Yup.object().shape({
    phone: Yup.string()
      .required(i18next.t('validation:required_field'))
      .test('wrong_format', i18next.t('validation:wrong_format'), (value) =>
        mobilePhoneNumberValid(value ?? ''),
      ),
    email: Yup.string()
      .required(i18next.t('validation:required_field'))
      .matches(emailRegExp, i18next.t('validation:wrong_format'))
      .test(
        'email-include-domain',
        i18next.t('validation:wrong_domain'),
        validateEmailDomain,
      ),
    agreements: Yup.object().shape({
      firstAgreement: Yup.bool(),
      secondAgreement: Yup.bool(),
      thirdAgreement: Yup.bool().isTrue(
        i18next.t('validation:agreement_required'),
      ),
    }),
  });

  const formik = useFormik<IdentificationForm>({
    initialValues: {
      agreements: {},
      email: '',
      phone: '',
      ...initialValues,
    },
    onSubmit,
    validationSchema,
  });

  useEffect(() => {
    formik.setStatus({ errors: {} });
    return () => {
      formik.setStatus({ errors: {} });
    };
  }, []);

  const onSelectAll = () => {
    const formikForm = formik;
    const nextValue = !Object.values(formikForm.values.agreements).every(
      (valueAgreement: boolean) => valueAgreement,
    );

    formik.setFieldValue('agreements', {
      firstAgreement: nextValue,
      secondAgreement: nextValue,
      thirdAgreement: nextValue,
      fourthAgreement: nextValue,
    });
  };

  const isSubmitEnabled = [].every(
    (agreement: string) => formik.values.agreements[agreement],
  );

  return (
    <Frame
      {...frameConfig}
      sectionID={AppRegistrationSection.Identification}
      bodyWrapperClassName={classes(
        layout['width-lg'],
        layout['frame-x-centered'],
        styles['frame-body-wrapper'],
      )}
    >
      <Spacer marginBottom={{ default: '40px', s: '35px' }}>
        <Title text={t('title')} data-test="css-Contact-Data-Title" />
      </Spacer>

      <form onSubmit={formik.handleSubmit} data-test="form">
        <div className={styles['inputs-wrapper']}>
          <InputActor
            className={styles.border}
            formik={formik}
            inputProps={{
              allowSpaces: false,
            }}
            name="email"
            label={t('emailPlaceholder')}
            data-test="id-Email"
            fieldGroupProps={{
              dataTestValidationText: 'emailInputValidationText validationText',
            }}
            analytics={{
              description: 'Email address',
            }}
          />
          <InputActor
            className={styles.border}
            formik={formik}
            name="phone"
            label={t('phonePlaceholder')}
            data-test="id-Phone"
            sanitizer={phoneSanitizer}
            fieldGroupProps={{
              dataTestValidationText: 'phoneInputValidationText validationText',
            }}
            analytics={{
              description: 'Phone number',
            }}
          />
        </div>

        <Button
          className={styles['select-btn']}
          dataTest="btn-select"
          leftIcon={<CheckIcon />}
          onClick={onSelectAll}
          outlined
        >
          <span className={styles['select-content-btn']}>{t('selectAll')}</span>
        </Button>

        {initialValues.agreements && (
          <div className={styles.agreements}>
            {Object.keys(initialValues.agreements).map((agreement: string) => (
              <CheckboxActor
                key={agreement}
                formik={formik}
                name={`agreements.${agreement}`}
                text={
                  <Trans
                    i18nKey={`identification:agreements.${agreement}`}
                    values={{ amount }}
                    components={createTransLinksComponents(handleModal)}
                  />
                }
                dataTest={`css-Agreement-${agreement}`}
                fieldGroupProps={{
                  dataTestValidationText: `${agreement}ValidationText validationText`,
                }}
                analytics={{
                  description: 'Agreement checkbox',
                }}
              />
            ))}
          </div>
        )}

        <ButtonActor
          buttonProps={{
            leftIcon: <LockIcon />,
            type: 'submit',
            fill: true,
            className: layout['width-sm'],
            loading,
            dataTest: 'css-Continue-Button',
            disabled: !isSubmitEnabled,
          }}
          formik={formik}
          analytics={{
            description: 'Continue button',
          }}
        >
          {t('continueButtonText')}
        </ButtonActor>
      </form>
      <Text
        className={styles.additional}
        dangerouslySetInnerHTML={{ __html: t('additionalInfo') }}
        dataTest="additional-text"
        kind="p"
        level={2}
        data-test="css-Additional-Info"
      />
      {!isInWebView() && (
        <Row className={styles['security-message']}>
          <div>
            <ShieldIcon data-test="icon-shield" />
          </div>
          <Text
            dataTest="details"
            text={t('details')}
            kind="span"
            level={3}
            data-test="css-Secure-Info"
          />
        </Row>
      )}
      {showModal && (
        <LegalTermsModal onClose={handleModal} amount={amount} term={term} />
      )}
    </Frame>
  );
}
